import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { useOutletContext, useParams } from "react-router-dom";
import { db } from "../firebase/firebase";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";
import "./AdminFeatureTogglePanel.scss";

const FEATURES = [
  {
    key: "isInviteSection",
    title: "Invite Section",
    description: "Show or hide the main invitation wording section.",
  },
  {
    key: "isCardComponent",
    title: "Invitation Card",
    description: "Show or hide the invitation card section.",
  },
  {
    key: "isCountDown",
    title: "Countdown",
    description: "Show or hide the wedding countdown section.",
  },
  {
    key: "isRsvp",
    title: "RSVP",
    description: "Show or hide the RSVP section.",
  },
  //   {
  //     key: "isAgenda",
  //     title: "Agenda",
  //     description: "Show or hide the event agenda section.",
  //   },
  {
    key: "isLoveNote",
    title: "Love Note",
    description: "Show or hide the love note section.",
  },
];

const AdminFeatureTogglePanel = () => {
  const { slug: routeSlug } = useParams();
  const { weddingData } = useOutletContext();

  const slug = routeSlug || weddingData?.slug || "";

  const [siteData, setSiteData] = useState(weddingData || {});
  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);
  const [featureState, setFeatureState] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const loadFreshSiteData = async () => {
    try {
      setThemeLoading(true);

      const fallbackData = weddingData || {};
      const fallbackTheme = sanitizeTheme(fallbackData?.theme || DEFAULT_THEME);

      if (!slug) {
        setSiteData(fallbackData);
        setSiteTheme(fallbackTheme);
        return;
      }

      const siteSnap = await getDoc(doc(db, "sites", slug));

      if (siteSnap.exists()) {
        const freshData = {
          id: siteSnap.id,
          slug,
          ...siteSnap.data(),
        };

        setSiteData(freshData);
        setSiteTheme(
          sanitizeTheme(freshData?.theme || weddingData?.theme || DEFAULT_THEME)
        );

        const freshFeatureState = FEATURES.reduce((acc, item) => {
          acc[item.key] = freshData?.[item.key] !== false;
          return acc;
        }, {});

        setFeatureState(freshFeatureState);
      } else {
        setSiteData(fallbackData);
        setSiteTheme(fallbackTheme);

        const fallbackFeatureState = FEATURES.reduce((acc, item) => {
          acc[item.key] = fallbackData?.[item.key] !== false;
          return acc;
        }, {});

        setFeatureState(fallbackFeatureState);
      }
    } catch (error) {
      console.error("Error loading fresh feature data:", error);

      const fallbackData = weddingData || {};
      setSiteData(fallbackData);
      setSiteTheme(sanitizeTheme(fallbackData?.theme || DEFAULT_THEME));

      const fallbackFeatureState = FEATURES.reduce((acc, item) => {
        acc[item.key] = fallbackData?.[item.key] !== false;
        return acc;
      }, {});

      setFeatureState(fallbackFeatureState);
    } finally {
      setThemeLoading(false);
    }
  };

  useEffect(() => {
    loadFreshSiteData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
  const syncLockedRsvp = async () => {
    try {
      if (!siteData) return;

      const shouldLockRsvp = !canControlRsvp(siteData);

      if (!shouldLockRsvp) return;

      if (siteData?.isRsvp === false) return;

      const payload = {
        isRsvp: false,
        updatedAt: serverTimestamp(),
      };

      const updateTasks = [];

      if (slug) {
        updateTasks.push(updateDoc(doc(db, "sites", slug), payload));
      }

      const userDocId = getUserDocId();

      if (userDocId) {
        updateTasks.push(updateDoc(doc(db, "users", userDocId), payload));
      }

      await Promise.all(updateTasks);

      setSiteData((prev) => ({
        ...prev,
        isRsvp: false,
      }));

      setFeatureState((prev) => ({
        ...prev,
        isRsvp: false,
      }));
    } catch (error) {
      console.error("Failed to sync locked RSVP:", error);
    }
  };

  syncLockedRsvp();
}, [siteData, slug]);

  const themeStyle = useMemo(
    () => createThemeStyle(siteTheme || DEFAULT_THEME),
    [siteTheme]
  );

  const getUserDocId = () => {
    return (
      siteData?.ownerUid ||
      siteData?.uid ||
      siteData?.userId ||
      siteData?.id ||
      weddingData?.ownerUid ||
      weddingData?.uid ||
      weddingData?.userId ||
      weddingData?.id ||
      ""
    );
  };

  const isTrialUser = (data) => {
    return (
      data?.plan === "trial" ||
      data?.isTrial === true
    );
  };

  const isPremiumUser = (data) => {
    return data?.plan === "premium";
  };

  const canControlRsvp = (data) => {
    if (isTrialUser(data)) return true;
    if (isPremiumUser(data)) return data?.addons?.digitalInvite === true;
    return false;
  };

  const handleToggleFeature = async (key) => {
    const currentValue = featureState[key] !== false;
    const nextValue = !currentValue;

    setSavingKey(key);
    setSuccessMessage("");
    setErrorMessage("");

    setFeatureState((prev) => ({
      ...prev,
      [key]: nextValue,
    }));

    setSiteData((prev) => ({
      ...prev,
      [key]: nextValue,
    }));

    try {
      const payload = {
        [key]: nextValue,
        updatedAt: serverTimestamp(),
      };

      const updateTasks = [];

      if (slug) {
        updateTasks.push(updateDoc(doc(db, "sites", slug), payload));
      }

      const userDocId = getUserDocId();



      if (userDocId) {
        updateTasks.push(updateDoc(doc(db, "users", userDocId), payload));
      }

      if (updateTasks.length === 0) {
        throw new Error("Missing site slug or user document id.");
      }

      await Promise.all(updateTasks);

      setSuccessMessage("Section visibility updated successfully.");
    } catch (error) {
      console.error("Section visibility update error:", error);

      setFeatureState((prev) => ({
        ...prev,
        [key]: currentValue,
      }));

      setSiteData((prev) => ({
        ...prev,
        [key]: currentValue,
      }));

      setErrorMessage("Failed to update section visibility. Please try again.");
    } finally {
      setSavingKey("");
    }
  };

  if (themeLoading || !siteTheme) return null;

  return (
    <div className="featureTogglePanel" style={themeStyle}>
      <div className="featureTogglePanel__card">
        <div className="featureTogglePanel__header">
          <p className="featureTogglePanel__eyebrow">Website Visibility</p>
          <h1 className="featureTogglePanel__title">Show / Hide Sections</h1>
          <p className="featureTogglePanel__subtitle">
            Control which sections appear on your public wedding invitation.
          </p>
        </div>

        <div className="featureTogglePanel__list">
          {FEATURES.map((feature) => {
            const isActive = featureState[feature.key] !== false;
            const isSaving = savingKey === feature.key;
            const isRsvpFeature = feature.key === "isRsvp";
            const isRsvpLocked = isRsvpFeature && !canControlRsvp(siteData);
            const finalIsActive = isRsvpLocked ? false : isActive;

            return (
              <div className="featureTogglePanel__item" key={feature.key}>
                <div className="featureTogglePanel__itemText">
                  <h2 className="featureTogglePanel__itemTitle">
                    {feature.title}
                  </h2>
                  <p className="featureTogglePanel__itemDescription">
                    {feature.description}
                  </p>
                </div>

                <div className="featureTogglePanel__itemAction">
                  <span
                    className={`featureTogglePanel__status ${finalIsActive ? "is-active" : "is-hidden"
                      }`}
                  >
                    {finalIsActive ? "Visible" : "Hidden"}
                  </span>

                  <button
                    type="button"
                    className={`featureTogglePanel__switch ${finalIsActive ? "featureTogglePanel__switch--active" : ""
                      }`}
                    onClick={() => handleToggleFeature(feature.key)}
                    disabled={Boolean(savingKey) || isRsvpLocked}
                    aria-pressed={finalIsActive}
                    aria-label={`Toggle ${feature.title}`}
                  >
                    <span className="featureTogglePanel__switchKnob" />
                  </button>
                  
                </div>
                {isRsvpLocked && (
                    <p className="featureTogglePanel__saving">
                      Digital Invite add-on is required to enable RSVP.
                    </p>
                  )}

                {isSaving && (
                  <p className="featureTogglePanel__saving">Saving...</p>
                )}
              </div>
            );
          })}
        </div>

        {errorMessage && (
          <p className="featureTogglePanel__message featureTogglePanel__message--error">
            {errorMessage}
          </p>
        )}

        {successMessage && (
          <p className="featureTogglePanel__message featureTogglePanel__message--success">
            {successMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminFeatureTogglePanel;